import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Box,
  Button,
  ButtonGroup,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Link,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DoDisturbOnOutlinedIcon from '@mui/icons-material/DoDisturbOnOutlined';
import FlightTakeoffRoundedIcon from '@mui/icons-material/FlightTakeoffRounded';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';

import { buildPublicFileUrl } from '../../config/env';
import { normalizeRoleKey } from '../../utils/roleCapabilities';
import {
  ConfirmDialog,
  DataTableShell,
  DetailField,
  EmptyState,
  FilterBar,
  PageHeader,
  PageLayout,
  StatCard,
  StatusBadge,
} from '../../components/ui';
import {
  cancelExitEntryRequest,
  fetchExitEntryRequests,
  updateRequestStatus,
} from '../../services/exitEntryApi';

const STATUS_OPTIONS = ['All', 'pending', 'approved', 'rejected', 'cancelled'];

const normalizeStatus = (status) => String(status || '').trim().toLowerCase();

const formatValue = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  return value;
};

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('en', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  }).format(date);
};

const labelize = (value) =>
  String(value || '')
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, (match) => match.toUpperCase())
    .trim();

const getRequesterName = (request) =>
  request?.user?.name
    || request?.employee?.name
    || request?.employee_name
    || request?.requester_name
    || 'N/A';

const getRequesterCode = (request) =>
  request?.user?.employee_code
    || request?.employee?.employee_code
    || request?.employee_code
    || '-';

const buildQueueParams = (viewMode) => {
  if (viewMode === 'assigned_requests') {
    return { view_mode: 'assigned_requests', queue: 'pending' };
  }
  if (viewMode === 'archive') {
    return { view_mode: 'assigned_requests', queue: 'archive' };
  }
  return {};
};

export default function ExitEntryListPage() {
  const theme = useTheme();
  const navigate = useNavigate();

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [role] = useState(localStorage.getItem('role') || 'employee');
  const normalizedRole = normalizeRoleKey(role);
  const isGlobalExitEntryAdmin =
    localStorage.getItem('is_platform_admin') === '1' ||
    ['hr_admin', 'admin', 'super_admin', 'system_admin'].includes(normalizedRole);
  const canViewAssigned = isGlobalExitEntryAdmin;
  const [viewMode, setViewMode] = useState('all');
  const [openView, setOpenView] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [confirm, setConfirm] = useState({
    open: false,
    id: null,
    action: null,
  });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  const loadRequests = async () => {
    setLoading(true);
    try {
      const data = await fetchExitEntryRequests(buildQueueParams(viewMode));
      setRequests(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching requests:', error);
      toast.error('Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode]);

  const askConfirm = (id, action) => setConfirm({ open: true, id, action });
  const closeConfirm = () => setConfirm({ open: false, id: null, action: null });
  const handleNewRequest = () => navigate('/exit-entry/new');

  const handleConfirm = async () => {
    if (!confirm.id || !confirm.action) return;
    setProcessingId(confirm.id);
    try {
      if (confirm.action === 'cancel') {
        await cancelExitEntryRequest(confirm.id);
        toast.success('Request cancelled successfully!');
      } else if (confirm.action === 'approve' || confirm.action === 'reject') {
        await updateRequestStatus(confirm.id, {
          status: confirm.action === 'approve' ? 'approved' : 'rejected',
        });
        toast.success(`Request ${confirm.action === 'approve' ? 'approved' : 'rejected'} successfully!`);
      }
      await loadRequests();
    } catch (error) {
      console.error('Error updating request:', error);
      toast.error('Operation failed');
    } finally {
      setProcessingId(null);
      closeConfirm();
    }
  };

  const handleViewModeChange = (_event, newValue) => {
    if (!newValue) return;
    setViewMode(newValue);
    setStatusFilter(newValue === 'assigned_requests' ? 'pending' : 'All');
  };

  const handleResetFilters = () => {
    setSearch('');
    setStatusFilter(viewMode === 'assigned_requests' ? 'pending' : 'All');
  };

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return requests.filter((request) => {
      const statusOk =
        statusFilter === 'All'
          ? true
          : normalizeStatus(request.status) === normalizeStatus(statusFilter);
      if (!term) return statusOk;
      const haystack = [
        getRequesterName(request),
        getRequesterCode(request),
        request?.visa_type,
        request?.validity_from_date,
        request?.validity_to_date,
        request?.country,
        request?.phone_number,
        request?.location_in_country,
        request?.reason,
      ].join(' ').toLowerCase();
      return statusOk && haystack.includes(term);
    });
  }, [requests, search, statusFilter]);

  const metrics = useMemo(() => {
    const total = requests.length;
    const pending = requests.filter((request) => normalizeStatus(request.status) === 'pending').length;
    const approved = requests.filter((request) => normalizeStatus(request.status) === 'approved').length;
    const rejected = requests.filter((request) => normalizeStatus(request.status) === 'rejected').length;
    return { total, pending, approved, rejected };
  }, [requests]);

  const onView = (request) => {
    setSelectedRequest(request);
    setOpenView(true);
  };

  const closeView = () => {
    setOpenView(false);
    setSelectedRequest(null);
  };

  const renderActions = (request) => {
    const isPending = normalizeStatus(request.status) === 'pending';
    const canHrAct = isGlobalExitEntryAdmin && isPending && viewMode !== 'archive';
    const canCancel = isPending && !isGlobalExitEntryAdmin;
    const busy = processingId === request.id;

    return (
      <Stack direction="row" spacing={0.75} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
        <Button
          variant="outlined"
          size="small"
          startIcon={<VisibilityOutlinedIcon />}
          onClick={() => onView(request)}
        >
          View
        </Button>
        {canHrAct ? (
          <>
            <Button
              variant="outlined"
              color="success"
              size="small"
              disabled={busy}
              startIcon={<CheckCircleOutlineRoundedIcon />}
              onClick={() => askConfirm(request.id, 'approve')}
            >
              Approve
            </Button>
            <Button
              variant="outlined"
              color="error"
              size="small"
              disabled={busy}
              startIcon={<CancelOutlinedIcon />}
              onClick={() => askConfirm(request.id, 'reject')}
            >
              Reject
            </Button>
          </>
        ) : null}
        {canCancel ? (
          <Button
            variant="text"
            color="warning"
            size="small"
            disabled={busy}
            startIcon={<DoDisturbOnOutlinedIcon />}
            onClick={() => askConfirm(request.id, 'cancel')}
          >
            Cancel
          </Button>
        ) : null}
      </Stack>
    );
  };

  const mobileCards = (
    <Stack spacing={1.4}>
      {filtered.map((request) => (
        <Box
          key={request.id}
          sx={{
            p: 1.4,
            borderRadius: theme.hr?.radius?.lg || 2,
            border: '1px solid',
            borderColor: theme.hr?.line?.soft || 'divider',
            backgroundColor:
              theme.palette.mode === 'dark'
                ? alpha(theme.palette.common.white, 0.04)
                : alpha(theme.palette.background.paper, 0.84),
          }}
        >
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1.2}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 850 }} noWrap>
                {getRequesterName(request)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatValue(request.visa_type)} · {formatDate(request.validity_from_date)}
              </Typography>
            </Box>
            <StatusBadge status={request.status} />
          </Stack>

          <Grid container spacing={1} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <DetailField compact label="From" value={formatDate(request.validity_from_date)} />
            </Grid>
            <Grid item xs={6}>
              <DetailField compact label="To" value={formatDate(request.validity_to_date)} />
            </Grid>
          </Grid>

          <Box sx={{ mt: 1.2 }}>{renderActions(request)}</Box>
        </Box>
      ))}
    </Stack>
  );

  const confirmMeta = {
    cancel: {
      title: 'Cancel Request',
      description: 'Are you sure you want to cancel this request? This action cannot be undone.',
      confirmLabel: 'Yes, cancel',
      color: 'warning',
    },
    approve: {
      title: 'Approve Request',
      description: 'Are you sure you want to approve this exit / entry request?',
      confirmLabel: 'Yes, approve',
      color: 'success',
    },
    reject: {
      title: 'Reject Request',
      description: 'Are you sure you want to reject this exit / entry request?',
      confirmLabel: 'Yes, reject',
      color: 'error',
    },
  }[confirm.action] || {
    title: 'Confirm',
    description: 'Are you sure you want to continue?',
    confirmLabel: 'Confirm',
    color: 'primary',
  };

  return (
    <PageLayout backgroundAccent>
      <PageHeader
        variant="dashboard"
        overline="Exit / entry"
        title="Exit / entry requests"
        subtitle="Create, view, and manage visa request status, supporting documents, and approval actions in one workspace."
        actions={
          <>
            <Button
              variant="outlined"
              startIcon={<RefreshRoundedIcon />}
              onClick={loadRequests}
              disabled={loading}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<AddRoundedIcon />}
              onClick={handleNewRequest}
            >
              New Request
            </Button>
          </>
        }
      />

      <Grid container spacing={1.8} sx={{ mb: 2.1 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            tone="dashboard"
            title="Total Requests"
            value={metrics.total}
            hint="Current queue"
            icon={<FlightTakeoffRoundedIcon />}
            color="primary.main"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            tone="dashboard"
            title="Pending"
            value={metrics.pending}
            hint="Waiting for action"
            icon={<DescriptionOutlinedIcon />}
            color="warning.main"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            tone="dashboard"
            title="Approved"
            value={metrics.approved}
            hint="Completed approvals"
            icon={<CheckCircleOutlineRoundedIcon />}
            color="success.main"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            tone="dashboard"
            title="Rejected"
            value={metrics.rejected}
            hint="Declined requests"
            icon={<CancelOutlinedIcon />}
            color="error.main"
            loading={loading}
          />
        </Grid>
      </Grid>

      <Stack spacing={1.5}>
        <FilterBar
          left={
            <>
              {canViewAssigned ? (
                <ButtonGroup size="small" variant="outlined" sx={{ flexShrink: 0 }}>
                  <Button
                    onClick={(event) => handleViewModeChange(event, 'all')}
                    variant={viewMode === 'all' ? 'contained' : 'outlined'}
                  >
                    All Requests
                  </Button>
                  <Button
                    onClick={(event) => handleViewModeChange(event, 'assigned_requests')}
                    variant={viewMode === 'assigned_requests' ? 'contained' : 'outlined'}
                  >
                    Assigned
                  </Button>
                  <Button
                    onClick={(event) => handleViewModeChange(event, 'archive')}
                    variant={viewMode === 'archive' ? 'contained' : 'outlined'}
                  >
                    Archive
                  </Button>
                </ButtonGroup>
              ) : null}

              <TextField
                size="small"
                placeholder="Search name, code, visa type, date..."
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                sx={{ minWidth: { xs: '100%', sm: 280 }, flex: 1 }}
                InputProps={{
                  startAdornment: <SearchRoundedIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
              <TextField
                size="small"
                select
                label="Status"
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
                sx={{ minWidth: { xs: '100%', sm: 170 } }}
              >
                {STATUS_OPTIONS.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status === 'All' ? 'All statuses' : labelize(status)}
                  </MenuItem>
                ))}
              </TextField>
            </>
          }
          onReset={handleResetFilters}
        />

        <DataTableShell
          tone="dashboard"
          title="Request list"
          subtitle="Validity windows, visa details, status, and available actions."
          loading={loading}
          empty={
            filtered.length === 0 ? (
              <EmptyState
                compact
                title={requests.length === 0 ? 'No exit / entry requests' : 'No matching requests'}
                description={
                  requests.length === 0
                    ? 'Create a new request when you need exit / entry support.'
                    : 'Try changing the search term or status filter.'
                }
                actionLabel={requests.length === 0 ? 'Create request' : undefined}
                onAction={requests.length === 0 ? handleNewRequest : undefined}
                icon={FlightTakeoffRoundedIcon}
              />
            ) : null
          }
          mobileCards={mobileCards}
        >
          <Table size="small" aria-label="Exit entry requests table">
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Visa Type</TableCell>
                <TableCell>Validity From</TableCell>
                <TableCell>Validity To</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((request) => (
                <TableRow key={request.id} hover>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: theme.hr?.radius?.sm || 1.5,
                          display: 'grid',
                          placeItems: 'center',
                          color: 'primary.main',
                          backgroundColor: alpha(theme.palette.primary.main, 0.1),
                          flexShrink: 0,
                        }}
                      >
                        <PersonOutlineRoundedIcon fontSize="small" />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 800 }} noWrap>
                          {getRequesterName(request)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          #{getRequesterCode(request)}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell>{formatValue(request.visa_type)}</TableCell>
                  <TableCell>{formatDate(request.validity_from_date)}</TableCell>
                  <TableCell>{formatDate(request.validity_to_date)}</TableCell>
                  <TableCell>
                    <StatusBadge status={request.status} />
                  </TableCell>
                  <TableCell align="right">{renderActions(request)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DataTableShell>
      </Stack>

      <Dialog open={openView} onClose={closeView} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Box>
              <Typography variant="h5">Exit / entry request details</Typography>
              <Typography variant="body2" color="text.secondary">
                Full visa request context and supporting document state.
              </Typography>
            </Box>
            <IconButton onClick={closeView} aria-label="Close request details">
              <CancelOutlinedIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {selectedRequest ? (
            <Stack spacing={1.5}>
              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                <StatusBadge status={selectedRequest.status} />
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                  Request #{selectedRequest.id}
                </Typography>
              </Stack>

              <Grid container spacing={1.2}>
                <Grid item xs={12} md={6}>
                  <DetailField
                    label="User Name"
                    value={getRequesterName(selectedRequest)}
                    icon={<PersonOutlineRoundedIcon fontSize="small" />}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DetailField
                    label="Visa Type"
                    value={selectedRequest.visa_type}
                    icon={<FlightTakeoffRoundedIcon fontSize="small" />}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DetailField
                    label="Validity From"
                    value={formatDate(selectedRequest.validity_from_date)}
                    icon={<CalendarMonthRoundedIcon fontSize="small" />}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DetailField
                    label="Validity To"
                    value={formatDate(selectedRequest.validity_to_date)}
                    icon={<CalendarMonthRoundedIcon fontSize="small" />}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <DetailField
                    label="Country"
                    value={selectedRequest.country}
                    icon={<PublicRoundedIcon fontSize="small" />}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <DetailField
                    label="Phone Number"
                    value={selectedRequest.phone_number}
                    icon={<PhoneOutlinedIcon fontSize="small" />}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <DetailField
                    label="Period"
                    value={selectedRequest.period_in_days ? `${selectedRequest.period_in_days} days` : '-'}
                    icon={<DescriptionOutlinedIcon fontSize="small" />}
                  />
                </Grid>
                <Grid item xs={12}>
                  <DetailField
                    label="Location In Country"
                    value={selectedRequest.location_in_country}
                    icon={<PlaceOutlinedIcon fontSize="small" />}
                  />
                </Grid>
                <Grid item xs={12}>
                  <DetailField
                    label="Reason"
                    value={selectedRequest.reason}
                    icon={<DescriptionOutlinedIcon fontSize="small" />}
                    sx={{ '& .MuiTypography-body2': { whiteSpace: 'pre-wrap' } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <DetailField
                    label="Document"
                    value={
                      selectedRequest.document_path ? (
                        <Link
                          href={buildPublicFileUrl(selectedRequest.document_path)}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                          sx={{ fontWeight: 800 }}
                        >
                          View document
                        </Link>
                      ) : (
                        'Attachment missing'
                      )
                    }
                    icon={<AttachFileRoundedIcon fontSize="small" />}
                  />
                </Grid>
                {selectedRequest.approval_attachment_path ? (
                  <Grid item xs={12}>
                    <DetailField
                      label="Approval Attachment"
                      value={
                        <Link
                          href={buildPublicFileUrl(selectedRequest.approval_attachment_path)}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                          sx={{ fontWeight: 800 }}
                        >
                          View approval attachment
                        </Link>
                      }
                      icon={<AttachFileRoundedIcon fontSize="small" />}
                    />
                  </Grid>
                ) : null}
              </Grid>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeView}>Close</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={confirm.open}
        title={confirmMeta.title}
        description={confirmMeta.description}
        confirmLabel={confirmMeta.confirmLabel}
        cancelLabel="No, go back"
        confirmColor={confirmMeta.color}
        loading={Boolean(processingId)}
        onConfirm={handleConfirm}
        onClose={closeConfirm}
      />
    </PageLayout>
  );
}
